/**
 * @fileoverview Pure section-tree model extracted from ReportTemplateEditor.tsx
 * (MOR-556). No React, no Convex mutations — grouping a flat section list
 * into a tree, deriving the contents-rail entries, and computing a
 * drag-and-drop reorder are all plain data transforms.
 */
import { arrayMove } from "@dnd-kit/sortable";
import type { Id } from "@cvx/_generated/dataModel";
import type { SectionAdherence } from "@cvx/jobs/reporting/adherence";

export type ContentType = "narrative" | "table" | "chart" | "attachment";
export type FillMode = "generate" | "attachment";

export interface Section {
  _id: Id<"reportTemplateSections">;
  order: number;
  parentSectionId?: Id<"reportTemplateSections">;
  title: string;
  guidance?: string;
  contentTypes: ContentType[];
  fillMode: FillMode;
  adherence?: SectionAdherence;
  adaptationNotes?: string;
  dataHints?: string[];
  // MOR-962 feature fields (source wording extraction).
  sourceWording?: string;
  draftingInstructions?: string;
  sourceWordingStatus?: "extracting" | "failed";
}

export interface ContentsEntry {
  id: Id<"reportTemplateSections">;
  title: string;
  nested: boolean;
}

export interface SectionTree {
  topLevel: Section[];
  childrenByParent: Map<string, Section[]>;
}

/** Groups a flat section list into top-level sections + their children,
 *  each sorted by `order`. Preserves input order for ties (stable sort). */
export function groupSectionsIntoTree(sections: Section[]): SectionTree {
  const childrenByParent = new Map<string, Section[]>();
  const topLevel: Section[] = [];
  for (const s of sections) {
    if (s.parentSectionId) {
      const arr = childrenByParent.get(s.parentSectionId) ?? [];
      arr.push(s);
      childrenByParent.set(s.parentSectionId, arr);
    } else {
      topLevel.push(s);
    }
  }
  topLevel.sort((a, b) => a.order - b.order);
  for (const arr of childrenByParent.values()) {
    arr.sort((a, b) => a.order - b.order);
  }
  return { topLevel, childrenByParent };
}

/** Contents-rail entries: top-level sections with their children indented
 *  immediately below, in tree order. */
export function buildContentsEntries(tree: SectionTree): ContentsEntry[] {
  const entries: ContentsEntry[] = [];
  for (const s of tree.topLevel) {
    entries.push({ id: s._id, title: s.title, nested: false });
    for (const c of tree.childrenByParent.get(s._id) ?? []) {
      entries.push({ id: c._id, title: c.title, nested: true });
    }
  }
  return entries;
}

/** The reordered id list for a dnd-kit drag-end, or `null` when the drag
 *  didn't move anything (dropped on itself, dropped outside a droppable,
 *  or either id is no longer in `ids`). Shared by the top-level section
 *  list and each card's child-section list. */
export function computeReorderedIds<T extends string>(
  ids: T[],
  activeId: T,
  overId: T | null | undefined,
): T[] | null {
  if (!overId || activeId === overId) return null;
  const from = ids.indexOf(activeId);
  const to = ids.indexOf(overId);
  if (from === -1 || to === -1) return null;
  return arrayMove(ids, from, to);
}
